export function getStaffAppointmentFilterContext({ additionalConfigs, accessType, userUid }) {
  if (accessType === 'OWNER') {
    return { filterEnabled: false, allowedStaffIds: null };
  }

  const filterEnabled =
    additionalConfigs?.accessTypeFilters?.staffAppointmentFilterEnabled === true &&
    accessType === 'STAFF';

  const specialStaffIds =
    filterEnabled && userUid
      ? additionalConfigs?.accessTypeFilters?.specialStaffFilters?.[userUid]
      : null;

  return {
    filterEnabled,
    allowedStaffIds: Array.isArray(specialStaffIds) ? specialStaffIds : null,
  };
}

export function isStaffVisibleToUser({
  staffKey,
  staff,
  userUid,
  filterEnabled,
  allowedStaffIds,
}) {
  if (!filterEnabled) return true;

  if (staff.uid === userUid) return true;

  if (allowedStaffIds?.length > 0) {
    return allowedStaffIds.includes(staffKey);
  }

  return false;
}

export function getFilteredStaffKeys(staffInfo, { additionalConfigs, accessType, userUid }) {
  if (!staffInfo || accessType === null) return [];

  if (accessType === 'STAFF' && additionalConfigs === null) return [];

  const { filterEnabled, allowedStaffIds } = getStaffAppointmentFilterContext({
    additionalConfigs,
    accessType,
    userUid,
  });

  const staffKeys = [];

  for (const staffKey in staffInfo) {
    if (staffKey === 'selectedStaff') continue;

    const staff = staffInfo[staffKey];
    if (staff.isHidden === true) continue;

    if (
      !isStaffVisibleToUser({
        staffKey,
        staff,
        userUid,
        filterEnabled,
        allowedStaffIds,
      })
    ) {
      continue;
    }

    staffKeys.push(staffKey);
  }

  return staffKeys;
}

// Filters a ScheduleManager staff list (items shaped { id, name, isHidden }) against the
// same access rules used by the calendar. Returns null while the data needed to evaluate
// access is still loading, so callers render their loading state instead of the full list.
export function filterStaffListByAccess(
  staffList,
  { staffInfo, additionalConfigs, accessType, userUid },
) {
  if (!staffList) return staffList;

  if (accessType === null || accessType === undefined) return null;
  if (accessType === 'STAFF' && !additionalConfigs) return null;

  const { filterEnabled, allowedStaffIds } = getStaffAppointmentFilterContext({
    additionalConfigs,
    accessType,
    userUid,
  });

  if (!filterEnabled) return staffList;
  if (!staffInfo) return null;

  return staffList.filter((staff) =>
    isStaffVisibleToUser({
      staffKey: staff.id,
      staff: staffInfo[staff.id] ?? {},
      userUid,
      filterEnabled,
      allowedStaffIds,
    }),
  );
}

export function getAllowedStaffKeys(staffInfo, { additionalConfigs, accessType, userUid }) {
  const { filterEnabled, allowedStaffIds } = getStaffAppointmentFilterContext({
    additionalConfigs,
    accessType,
    userUid,
  });

  if (!staffInfo || !filterEnabled) return null;

  const allowedStaffKeys = [];

  for (const staffKey in staffInfo) {
    if (staffKey === 'selectedStaff') continue;

    const staff = staffInfo[staffKey];
    if (staff.isHidden === true) continue;

    if (
      isStaffVisibleToUser({
        staffKey,
        staff,
        userUid,
        filterEnabled,
        allowedStaffIds,
      })
    ) {
      allowedStaffKeys.push(staffKey);
    }
  }

  return allowedStaffKeys;
}

export function getEffectiveSelectedStaffKey({
  staffInfo,
  currentSelectedStaff,
  additionalConfigs,
  accessType,
  userUid,
}) {
  const allowedStaffKeys = getAllowedStaffKeys(staffInfo, {
    additionalConfigs,
    accessType,
    userUid,
  });

  if (allowedStaffKeys) {
    if (currentSelectedStaff && allowedStaffKeys.includes(currentSelectedStaff)) {
      return currentSelectedStaff;
    }

    return allowedStaffKeys[0] ?? null;
  }

  const ownStaffKey = Object.entries(staffInfo).find(([, value]) => value.uid === userUid)?.[0];

  const defaultSelectedStaff =
    ownStaffKey === undefined
      ? Object.entries(staffInfo).filter((staff) => staff[1].isHidden !== true)[0]?.[0]
      : ownStaffKey;

  return currentSelectedStaff || defaultSelectedStaff;
}
